import React from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { fetchTransferRequestsPage, type TransferRequestRow } from '@/data/transferRequests';
import {
  ALL_SITE_FILTER,
  normalizeSiteFilterChange,
  readSessionFilterState,
  resolveSiteFilterLocationIds,
  summarizeSiteFilterSelection,
  writeSessionFilterState,
} from '@/domain/library';
import { formatDay, formatCount } from '@/domain/warehouse';
import useSessionStore from '../../../state/session';
import useActiveLocationStore from '../../../state/locationScope';
import { acceptTransferBroadcast, rejectTransferBroadcast } from '../../../api/transfers';
import { invalidateAvailabilityQueries } from '../../../hooks/mutations/useInventoryMutations';
import { useToasts } from '../../../ui/ToastProvider';
import SearchableMultiSelect from '../../../components/SearchableMultiSelect';
import StageFilterToolbar from '../../../components/events/StageFilterToolbar';
import GroupedDataTable, { type GroupedTableColumn } from '../../../components/common/GroupedDataTable';
import LibraryPager from '../../../components/common/LibraryPager';
import LibrarySearchInput from '../../../components/common/LibrarySearchInput';
import LibraryFilterSheet, { LibraryFilterSheetGroup } from '../../../components/common/LibraryFilterSheet';
import LibraryRecordCard, {
  LibraryRecordCardDetail,
  LibraryRecordCardList,
  LibraryRecordCardSkeleton,
} from '../../../components/common/LibraryRecordCard';
import ErrorBanner from '../../../components/ErrorBanner';
import UpdateTransferBroadcastModal from '../../../components/transfers/UpdateTransferBroadcastModal';
import { StatusChip } from '../../../components/status/StatusChip';
import { LibraryAdvancedToggle } from '../../../ui/LibraryAdvancedToggle';
import { LibraryFilterToggle } from '../../../ui/LibraryFilterToggle';
import { LibraryViewToggle } from '../../../ui/LibraryViewToggle';
import WarehouseHero from './WarehouseHero';
import { buildStatusFilterModel } from './warehouseStatusFilters';
import { warehouseRecordHref } from './warehouseTabs';
import { WAREHOUSE_PAGE_SIZE, type WarehouseTabContext } from './shared';
import '../../../components/library/LibraryList.css';

const STATUS_MODEL = buildStatusFilterModel('transferRequest');
const SESSION_KEY = 'warehouse.requests.filters.session';

type RequestsFilters = {
  search: string;
  fromSites: string[];
  toSites: string[];
  statuses: string[];
  view: 'list' | 'grid';
  needByFrom: string;
  needByTo: string;
  createdFrom: string;
  createdTo: string;
};

const DEFAULT_FILTERS: RequestsFilters = {
  search: '',
  fromSites: [ALL_SITE_FILTER],
  toSites: [ALL_SITE_FILTER],
  statuses: STATUS_MODEL.defaults,
  view: 'list',
  needByFrom: '',
  needByTo: '',
  createdFrom: '',
  createdTo: '',
};

function readStoredFilters(profileId: string | null): RequestsFilters {
  const stored = readSessionFilterState<Partial<RequestsFilters>>(SESSION_KEY, profileId);
  if (!stored || typeof stored !== 'object') return DEFAULT_FILTERS;
  const list = (value: unknown, fallback: string[]) => (Array.isArray(value) ? value.map(String) : fallback);
  const text = (value: unknown) => (typeof value === 'string' ? value : '');
  return {
    search: text(stored.search),
    fromSites: list(stored.fromSites, DEFAULT_FILTERS.fromSites),
    toSites: list(stored.toSites, DEFAULT_FILTERS.toSites),
    statuses: list(stored.statuses, DEFAULT_FILTERS.statuses),
    view: stored.view === 'grid' ? 'grid' : 'list',
    needByFrom: text(stored.needByFrom),
    needByTo: text(stored.needByTo),
    createdFrom: text(stored.createdFrom),
    createdTo: text(stored.createdTo),
  };
}

type RequestView = {
  row: TransferRequestRow;
  eventId: string | null;
  eventCode: string;
  eventName: string;
  itemId: string | null;
  itemCode: string;
  itemName: string;
  qty: number | null;
  qtyAvailable: number | null;
  status: string;
  canAcceptReject: boolean;
  canEdit: boolean;
};

export default function RequestsTab({ context }: { context: WarehouseTabContext }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const sessionReady = useSessionStore((state) => state.sessionReady);
  const allowedLocationIds = useSessionStore((state) => state.allowedLocationIds);
  const homeLocationId = useSessionStore((state) => state.profile?.home_location_id ?? null);
  const activeLocationId = useActiveLocationStore((state) => state.activeLocationId);

  const [filters, setFilters] = React.useState<RequestsFilters>(() => readStoredFilters(context.profileId));
  const [debouncedSearch, setDebouncedSearch] = React.useState(filters.search.trim());
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<TransferRequestRow | null>(null);
  // One answer at a time: a double click must not send Accept twice.
  const [pendingId, setPendingId] = React.useState<string | null>(null);
  const filterToggleRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    const next = filters.search.trim();
    if (next === debouncedSearch) return undefined;
    const handle = window.setTimeout(() => setDebouncedSearch(next), 250);
    return () => window.clearTimeout(handle);
  }, [debouncedSearch, filters.search]);
  React.useEffect(() => {
    writeSessionFilterState(SESSION_KEY, context.profileId, filters);
  }, [context.profileId, filters]);

  // Any filter change starts again at page 1 in the same render.
  const signature = JSON.stringify([
    debouncedSearch,
    filters.fromSites,
    filters.toSites,
    filters.statuses,
    filters.needByFrom,
    filters.needByTo,
    filters.createdFrom,
    filters.createdTo,
    context.linkedEvent?.id ?? null,
  ]);
  const [pageState, setPageState] = React.useState(() => ({ signature, page: 1 }));
  const page = pageState.signature === signature ? pageState.page : 1;
  const setPage = React.useCallback((next: number) => setPageState({ signature, page: Math.max(1, next) }), [signature]);

  const update = React.useCallback((patch: Partial<RequestsFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
  }, []);
  const noStatuses = filters.statuses.length === 0;
  // Every status picked sends the whole list too: with no status filter the planner checks the
  // tables' row-level security once per joined row, which took about 8 s on 48 requests.
  const statusesParam = React.useMemo(() => {
    if (noStatuses) return null;
    return STATUS_MODEL.statuses.every((status) => filters.statuses.includes(status))
      ? [...STATUS_MODEL.statuses]
      : filters.statuses;
  }, [filters.statuses, noStatuses]);

  // The viewer's sites: the active location, or every site they may use (plus home).
  const myLocationIds = React.useMemo(() => {
    const ids = new Set<string>();
    if (activeLocationId) ids.add(activeLocationId);
    else allowedLocationIds.forEach((id) => { if (id) ids.add(id); });
    if (homeLocationId) ids.add(homeLocationId);
    return Array.from(ids);
  }, [activeLocationId, allowedLocationIds, homeLocationId]);
  const fromLocationIds = React.useMemo(
    () => resolveSiteFilterLocationIds(filters.fromSites, context.regionFilterOptions) ?? null,
    [context.regionFilterOptions, filters.fromSites],
  );
  const toLocationIds = React.useMemo(
    () => resolveSiteFilterLocationIds(filters.toSites, context.regionFilterOptions) ?? null,
    [context.regionFilterOptions, filters.toSites],
  );

  const query = useQuery({
    queryKey: [
      'transfer-requests',
      myLocationIds,
      allowedLocationIds,
      page,
      fromLocationIds,
      toLocationIds,
      statusesParam,
      filters.needByFrom,
      filters.needByTo,
      filters.createdFrom,
      filters.createdTo,
      debouncedSearch,
      context.linkedEvent?.id ?? '',
    ],
    queryFn: () => fetchTransferRequestsPage({
      activeLocationIds: myLocationIds,
      allowedLocationIds,
      offset: (page - 1) * WAREHOUSE_PAGE_SIZE,
      pageSize: WAREHOUSE_PAGE_SIZE,
      search: debouncedSearch,
      fromLocationIds,
      toLocationIds,
      needByFrom: filters.needByFrom || undefined,
      needByTo: filters.needByTo || undefined,
      createdFrom: filters.createdFrom || undefined,
      createdTo: filters.createdTo || undefined,
      linkedEventId: context.linkedEvent?.id ?? null,
      statuses: statusesParam,
    }),
    enabled: sessionReady && !noStatuses && (myLocationIds.length > 0 || allowedLocationIds.length > 0),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const total = noStatuses ? 0 : query.data?.count ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / WAREHOUSE_PAGE_SIZE));
  React.useEffect(() => {
    if (query.data && total > 0 && page > pageCount) setPage(pageCount);
  }, [page, pageCount, query.data, setPage, total]);
  const views: RequestView[] = React.useMemo(() => (query.data?.data ?? []).map((row: TransferRequestRow) => {
    const event = row.shortage?.event_line?.event;
    const status = String(row.status || 'open');
    const isRequestingSite = Boolean(row.isRequestingSite);
    const requestingLocationId = row.shortage?.location_id ?? null;
    const candidateLocationId = row.to_location_id ?? null;
    const rawQty = row.qty ?? row.shortage?.qty_requested ?? null;
    const qty = rawQty == null ? null : Number(rawQty);
    return {
      row,
      eventId: event?.id ?? null,
      eventCode: event?.event_code ?? '—',
      eventName: event?.name ?? '',
      itemId: row.item_id ?? row.shortage?.item_id ?? null,
      itemCode: row.item?.item_code ?? '—',
      itemName: row.item?.name_internal ?? '',
      qty: qty != null && Number.isFinite(qty) ? qty : null,
      qtyAvailable: row.qty_available == null ? null : Number(row.qty_available),
      status,
      // The site asked to fill a request answers it; the requesting site edits it.
      canAcceptReject: status === 'open' && Boolean(candidateLocationId) && myLocationIds.includes(candidateLocationId) && !isRequestingSite,
      canEdit: isRequestingSite && status === 'open' && Boolean(requestingLocationId) && myLocationIds.includes(requestingLocationId),
    };
  }), [myLocationIds, query.data]);

  const pendingRef = React.useRef<string | null>(null);
  const handleAccept = React.useCallback(async (broadcastId: string) => {
    if (pendingRef.current) return;
    pendingRef.current = broadcastId;
    setPendingId(broadcastId);
    try {
      const result = await acceptTransferBroadcast(broadcastId);
      push({ type: 'success', message: 'Request accepted. Transfer created.' });
      void queryClient.invalidateQueries({ queryKey: ['transfer-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['warehouse'] });
      invalidateAvailabilityQueries(queryClient);
      if (result?.transfer_order_id) navigate(warehouseRecordHref.transfer(result.transfer_order_id));
    } catch (error) {
      push({ type: 'error', message: error instanceof Error ? error.message : 'The request could not be accepted.' });
    } finally {
      pendingRef.current = null;
      setPendingId(null);
    }
  }, [navigate, push, queryClient]);
  const handleDecline = React.useCallback(async (broadcastId: string) => {
    if (pendingRef.current) return;
    pendingRef.current = broadcastId;
    setPendingId(broadcastId);
    try {
      await rejectTransferBroadcast(broadcastId);
      push({ type: 'success', message: 'Request declined.' });
      void queryClient.invalidateQueries({ queryKey: ['transfer-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['warehouse'] });
    } catch (error) {
      push({ type: 'error', message: error instanceof Error ? error.message : 'The request could not be declined.' });
    } finally {
      pendingRef.current = null;
      setPendingId(null);
    }
  }, [push, queryClient]);

  const renderActions = (view: RequestView) => {
    if (view.canAcceptReject) {
      const busy = pendingId != null;
      return (
        <span className="warehouse-row-actions">
          <button
            type="button"
            className="btn micro btn-primary"
            disabled={busy}
            aria-busy={pendingId === view.row.broadcast_id}
            onClick={() => { void handleAccept(view.row.broadcast_id); }}
          >
            Accept
          </button>
          <button type="button" className="btn micro" disabled={busy} onClick={() => { void handleDecline(view.row.broadcast_id); }}>
            Decline
          </button>
        </span>
      );
    }
    if (view.canEdit) {
      return <button type="button" className="btn micro" onClick={() => setEditing(view.row)}>Edit</button>;
    }
    return null;
  };

  const availabilityTone = (view: RequestView): string | undefined => {
    if (view.qtyAvailable == null || view.qty == null) return undefined;
    if (view.qtyAvailable >= view.qty) return 'ok';
    return view.qtyAvailable > 0 ? 'warn' : 'short';
  };

  const columns: GroupedTableColumn<RequestView>[] = [
    {
      key: 'event',
      header: 'Event',
      render: (view) => (
        <>
          {view.eventId ? (
            <Link className="library-list-doc__link" to={warehouseRecordHref.event(view.eventId)}>{view.eventCode}</Link>
          ) : <span>{view.eventCode}</span>}
          {view.eventName ? <span className="grouped-data-table__sub">{view.eventName}</span> : null}
        </>
      ),
    },
    {
      key: 'item',
      header: 'Item',
      render: (view) => (
        <>
          <span>{view.itemCode}</span>
          {view.itemName ? <span className="grouped-data-table__sub">{view.itemName}</span> : null}
        </>
      ),
    },
    { key: 'qty', header: 'Requested', width: '6.5rem', align: 'end', render: (view) => <span className="num">{view.qty == null ? '—' : formatCount(view.qty)}</span> },
    {
      key: 'free',
      header: 'Free',
      width: '5.5rem',
      align: 'end',
      render: (view) => <span className="num warehouse-availability" data-tone={availabilityTone(view)}>{view.qtyAvailable == null ? '—' : formatCount(view.qtyAvailable)}</span>,
    },
    {
      key: 'dates',
      header: 'Need by → return',
      width: '9.5rem',
      render: (view) => <span className="num">{[formatDay(view.row.needed_by_at), formatDay(view.row.expected_return_at)].filter(Boolean).join(' → ') || '—'}</span>,
    },
    { key: 'site', header: 'Fulfilling site', optional: true, render: (view) => view.row.to_location?.name ?? '—' },
    { key: 'status', header: 'Status', width: '8rem', render: (view) => <StatusChip kind="transferRequest" status={view.status} /> },
    { key: 'actions', header: <span className="sr-only">Actions</span>, width: '9.5rem', className: 'grouped-data-table__menu-cell', render: renderActions },
  ];

  const renderCard = (view: RequestView) => {
    const href = view.eventId ? warehouseRecordHref.event(view.eventId) : '/transfers?tab=requests';
    return (
      <LibraryRecordCard
        key={view.row.broadcast_id}
        href={href}
        onOpen={() => navigate(href)}
        code={view.eventCode}
        badges={<StatusChip kind="transferRequest" status={view.status} size="sm" className="library-record-card__pill" />}
        title={view.eventName || view.itemName || 'Transfer request'}
        details={(
          <>
            <LibraryRecordCardDetail>{`${view.itemCode}${view.itemName ? ` · ${view.itemName}` : ''}`}</LibraryRecordCardDetail>
            <LibraryRecordCardDetail muted>{`Need by ${formatDay(view.row.needed_by_at) ?? '—'}`}</LibraryRecordCardDetail>
          </>
        )}
        meta={[
          view.qty == null ? null : `Requested ${formatCount(view.qty)}`,
          view.qtyAvailable == null ? null : `Free ${formatCount(view.qtyAvailable)}`,
        ]}
        actions={renderActions(view)}
      />
    );
  };

  const handleFromChange = React.useCallback(function handleFromChange(values: string[]) {
    update({ fromSites: normalizeSiteFilterChange(values, filters.fromSites) });
  }, [filters.fromSites, update]);
  const handleToChange = React.useCallback(function handleToChange(values: string[]) {
    update({ toSites: normalizeSiteFilterChange(values, filters.toSites) });
  }, [filters.toSites, update]);
  const handleStatusesChange = React.useCallback(function handleStatusesChange(statuses: string[]) {
    update({ statuses });
  }, [update]);
  const selectedStatusSet = React.useMemo(() => new Set(filters.statuses), [filters.statuses]);

  const fromControl = (
    <SearchableMultiSelect
      options={context.siteOptions}
      selected={filters.fromSites}
      onChange={handleFromChange}
      placeholder="All sites"
      ariaLabel="Requesting site"
      className="events-site-filter"
      selectionSummary={summarizeSiteFilterSelection(filters.fromSites, context.siteOptions, 'All sites')}
      showPills={false}
    />
  );
  const toControl = (
    <SearchableMultiSelect
      options={context.siteOptions}
      selected={filters.toSites}
      onChange={handleToChange}
      placeholder="All sites"
      ariaLabel="Fulfilling site"
      className="events-site-filter"
      selectionSummary={summarizeSiteFilterSelection(filters.toSites, context.siteOptions, 'All sites')}
      showPills={false}
    />
  );
  const statusControl = (
    <StageFilterToolbar
      selectedStatuses={selectedStatusSet}
      onSelectedStatusesChange={handleStatusesChange}
      stageGroups={STATUS_MODEL.stageGroups}
      standaloneStatuses={[]}
      orderStageLookup={STATUS_MODEL.lookup}
      defaultSelectedStatuses={STATUS_MODEL.defaults}
      label="Status"
      pluralNoun="statuses"
      allLabel="All statuses"
      defaultSelectionLabel="Open"
      compact
    />
  );
  const statusFilterActive = filters.statuses.length !== STATUS_MODEL.defaults.length
    || STATUS_MODEL.defaults.some((status) => !filters.statuses.includes(status));
  const advancedActiveCount = [filters.needByFrom, filters.needByTo, filters.createdFrom, filters.createdTo].filter(Boolean).length;
  const advancedActive = advancedActiveCount > 0;
  const sheetActiveCount = Number(!filters.fromSites.includes(ALL_SITE_FILTER))
    + Number(!filters.toSites.includes(ALL_SITE_FILTER))
    + Number(statusFilterActive)
    + advancedActiveCount;
  const errorMessage = noStatuses ? null : query.error instanceof Error ? query.error.message : query.error ? 'Requests could not be loaded.' : null;
  const loading = !noStatuses && query.isLoading && !query.data;
  const lastShown = Math.min(page * WAREHOUSE_PAGE_SIZE, total);
  const firstShown = total > 0 ? Math.min((page - 1) * WAREHOUSE_PAGE_SIZE + 1, lastShown) : 0;
  const resetDates = () => update({ needByFrom: '', needByTo: '', createdFrom: '', createdTo: '' });
  const dateFields = (
    <>
      <div className="library-toolbar__field text-xxs fg-muted">
        <span>Need by</span>
        <div className="library-toolbar__range">
          <input type="date" aria-label="Need by from" value={filters.needByFrom} onChange={(event) => update({ needByFrom: event.target.value })} />
          <span aria-hidden="true">to</span>
          <input type="date" aria-label="Need by to" value={filters.needByTo} onChange={(event) => update({ needByTo: event.target.value })} />
        </div>
      </div>
      <div className="library-toolbar__field text-xxs fg-muted">
        <span>Created</span>
        <div className="library-toolbar__range">
          <input type="date" aria-label="Created from" value={filters.createdFrom} onChange={(event) => update({ createdFrom: event.target.value })} />
          <span aria-hidden="true">to</span>
          <input type="date" aria-label="Created to" value={filters.createdTo} onChange={(event) => update({ createdTo: event.target.value })} />
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="settings-page__pinned">
        <WarehouseHero
          isPhone={context.isPhone}
          statsLabel="Request stats"
          stats={[{
            key: 'open',
            label: 'Requests',
            value: query.data || noStatuses ? formatCount(total) : '…',
            hint: 'To or from your sites',
          }]}
          actions={null}
        />
        {context.tabStrip}
        <div className="library-toolbar-block library-list-toolbar-block" data-open={advancedOpen && !context.compactToolbar} data-active={advancedActive}>
          <div className="card soft library-toolbar library-list-toolbar">
            <div className="library-toolbar__search">
              <LibrarySearchInput value={filters.search} onChange={(value) => update({ search: value })} placeholder="Event, item, SKU or site" label="Search requests" />
              {context.compactToolbar ? (
                <LibraryFilterToggle ref={filterToggleRef} expanded={sheetOpen} activeCount={sheetActiveCount} aria-haspopup="dialog" onClick={() => setSheetOpen(true)} />
              ) : null}
            </div>
            {!context.compactToolbar ? (
              <div className="library-toolbar__filters library-list-toolbar__filters">
                <div className="library-toolbar__field library-toolbar__field--site text-xxs fg-muted"><span>Requested by</span>{fromControl}</div>
                <div className="library-toolbar__field library-toolbar__field--site text-xxs fg-muted"><span>Fulfilling site</span>{toControl}</div>
                <div className="library-toolbar__field library-toolbar__field--compact text-xxs fg-muted">{statusControl}</div>
              </div>
            ) : null}
            <div className="library-toolbar__meta">
              <div className="lib-count" aria-live="polite">
                {loading ? 'Loading requests…' : `Showing ${formatCount(firstShown)}–${formatCount(lastShown)} of ${formatCount(total)} ${total === 1 ? 'request' : 'requests'}`}
                {context.linkedEvent ? (
                  <button type="button" className="library-list-linked-chip" onClick={context.onClearLinkedEvent}>
                    {`Linked to ${context.linkedEvent.code ?? 'event'}`} <span aria-hidden="true">{'×'}</span>
                  </button>
                ) : null}
              </div>
              {/* Phones show the same cards for List and Grid, so they have no view switch. */}
              {context.isPhone ? null : (
                <LibraryViewToggle
                  value={filters.view}
                  options={[{ value: 'list' as const, label: 'List' }, { value: 'grid' as const, label: 'Grid' }]}
                  onChange={(view) => update({ view })}
                  ariaLabel="Requests view"
                />
              )}
            </div>
            {!context.compactToolbar ? (
              <LibraryAdvancedToggle expanded={advancedOpen} active={advancedActive} onClick={() => setAdvancedOpen((open) => !open)} />
            ) : null}
          </div>
          {!context.compactToolbar ? (
            <div className="library-toolbar__advanced" data-open={advancedOpen}>
              <div className="library-toolbar__advanced-grid">{dateFields}</div>
              <div className="library-toolbar__advanced-actions">
                <button type="button" className="btn micro" disabled={!advancedActive} onClick={resetDates}>
                  Reset filters
                </button>
              </div>
            </div>
          ) : null}
        </div>
        <LibraryFilterSheet
          open={sheetOpen && context.compactToolbar}
          onClose={() => setSheetOpen(false)}
          onReset={() => update({
            fromSites: [ALL_SITE_FILTER],
            toSites: [ALL_SITE_FILTER],
            statuses: STATUS_MODEL.defaults,
            needByFrom: '',
            needByTo: '',
            createdFrom: '',
            createdTo: '',
          })}
          resetDisabled={sheetActiveCount === 0}
          resultLabel={`Show ${formatCount(total)} ${total === 1 ? 'request' : 'requests'}`}
          returnFocusRef={filterToggleRef}
        >
          <LibraryFilterSheetGroup label="Requested by">{fromControl}</LibraryFilterSheetGroup>
          <LibraryFilterSheetGroup label="Fulfilling site">{toControl}</LibraryFilterSheetGroup>
          <LibraryFilterSheetGroup label="Status">{statusControl}</LibraryFilterSheetGroup>
          <LibraryFilterSheetGroup label="More filters">
            <div className="library-list-sheet-advanced">{dateFields}</div>
          </LibraryFilterSheetGroup>
        </LibraryFilterSheet>
      </div>

      <main className="settings-content warehouse-content">
        <div className="library-list-body" role="tabpanel" id={context.panelId} aria-labelledby={context.tabElementId('requests')}>
          {errorMessage ? <ErrorBanner message={errorMessage} onRetry={() => { void query.refetch(); }} /> : null}
          {noStatuses ? (
            <div className="library-list-empty">
              <strong>Pick at least one status to see requests.</strong>
            </div>
          ) : context.isPhone || filters.view === 'grid' ? (
            <div className={filters.view === 'grid' && !context.isPhone ? 'library-list-card-grid' : undefined}>
              {loading ? (
                <LibraryRecordCardList label="Loading requests" busy>
                  {[0, 1, 2].map((index) => <LibraryRecordCardSkeleton key={index} />)}
                </LibraryRecordCardList>
              ) : views.length === 0 ? (
                <div className="library-list-empty">
                  <strong>No transfer requests</strong>
                  <span>Requests for your sites appear here when an event is short of an item another site has.</span>
                </div>
              ) : (
                <LibraryRecordCardList label="Transfer requests">{views.map(renderCard)}</LibraryRecordCardList>
              )}
            </div>
          ) : (
            <GroupedDataTable
              ariaLabel="Transfer requests"
              columns={columns}
              rows={views}
              getRowKey={(view) => String(view.row.broadcast_id)}
              emptyState={loading ? 'Loading requests…' : 'No transfer requests for your sites.'}
            />
          )}
          {!noStatuses ? (
            <LibraryPager page={Math.min(page, pageCount)} pageSize={WAREHOUSE_PAGE_SIZE} total={total} onPageChange={setPage} ariaLabel="Request pages" />
          ) : null}
        </div>
      </main>

      <UpdateTransferBroadcastModal
        open={Boolean(editing)}
        broadcast={editing}
        onClose={() => setEditing(null)}
        onUpdated={() => { void queryClient.invalidateQueries({ queryKey: ['transfer-requests'] }); }}
      />
    </>
  );
}
